import React, { useEffect, useState } from 'react';
import { Alert, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { signOut } from 'aws-amplify/auth';
import { useAuth } from '../../context/AuthContext';
import { AppButton, Panel } from '../../components/ui';
import AccountsAPI from '../../services/AccountsAPI';
import { creatorEnabled } from '../../services/CreatorAPI';
import './Creator.scss';
import useCreatorPageTitle from './useCreatorPageTitle';

const permissionLabels = { 'creator:read': 'Inspect content in Creator sessions you start.', 'creator:edit': 'Edit elements, environments, conditions, and behaviors in the live working copy.', 'creator:save': 'Save edits automatically with version history, and flush pending changes when you request Save.', 'creator:voice': 'Read voice messages you send from Creator.' };
export default function CreatorAuthorize() {
  useCreatorPageTitle('Connect your agent');
  const location = useLocation(); const navigate = useNavigate(); const auth = useAuth();
  const request = new URLSearchParams(location.search).get('request');
  const [details, setDetails] = useState(null); const [error, setError] = useState(''); const [busy, setBusy] = useState(false);
  useEffect(() => {
    let cancelled = false;
    if (!creatorEnabled || !/^[A-Za-z0-9_-]{43}$/.test(request || '')) { setError('This authorization request is invalid. Start again from your agent.'); return; }
    AccountsAPI.get(`/creator-oauth/requests/${request}`, { requiresAuth: true }).then(r => { if (!cancelled) setDetails(r.data); }).catch(e => { if (!cancelled) setError(e.response?.data?.error || 'Could not load the authorization request. Please try again.'); });
    return () => { cancelled = true; };
  }, [request, auth.userId]);
  async function decide(allow) {
    setBusy(true); setError('');
    try {
      const { data } = await AccountsAPI.post(`/creator-oauth/requests/${request}/decision`, { allow, approvalToken: details.approvalToken }, { requiresAuth: true });
      const callback = new URL(data.redirectUrl);
      if (callback.protocol !== 'http:' || !['localhost', '127.0.0.1'].includes(callback.hostname) || callback.pathname !== '/callback') throw new Error('Invalid agent callback');
      window.location.assign(callback.href);
    } catch (e) { setError(e.response?.data?.error || e.message); setBusy(false); }
  }
  async function switchAccount() { await signOut(); navigate('/login', { state: { from: location }, replace: true }); }
  return <main className="creator-page"><Panel row={false} className="creator-panel">
    <Typography variant="h1">{details ? `Allow ${details.name} to access 10k Creator?` : 'Connect your agent'}</Typography>
    <p>Signed in as <strong>{auth.email || auth.userName}</strong>.</p>
    <AppButton kind="ghost" onClick={switchAccount} disabled={busy}>Switch account</AppButton>
    {error && <Alert severity="error">{error}</Alert>}
    {details && <><p>Continue only if you started this connection from your agent. The client identifies itself as {details.name}.</p><ul>{details.scopes.map(scope => <li key={scope}>{permissionLabels[scope]}</li>)}</ul>
      <p>Your agent can act only in sessions you start. You can revoke this authorization from Connected agents.</p>
      <div className="creator-actions"><AppButton kind="primary" disabled={busy} onClick={() => decide(true)}>Allow</AppButton><AppButton kind="secondary" disabled={busy} onClick={() => decide(false)}>Cancel</AppButton></div>
    </>}
  </Panel></main>;
}
